/**
 * The app's one persistent top bar, full width in the logo's blue: menu button
 * (below xl), the GRAutomates logo and name, the notification bell, and the
 * signed-in person's profile menu (Settings, Log out).
 * ---------------------------------------------------------------------------
 * Rendered once by App.tsx above the sidebar and the page, so every page gets
 * it and no page has to leave room for it. The sidebar starts under it. Page
 * headers stay the page's own; this bar carries only what belongs to the
 * person, not the page.
 *
 * The menu button is the ONE control that shows or hides navigation, at every
 * width. Below xl it opens and closes the drawer (which opens under the bar, so
 * the button stays reachable). At xl and up it collapses the pinned sidebar and
 * brings it back; open is the default and the choice is remembered per browser
 * (SidebarContext). "[" does the same from the keyboard. Do not add a second
 * show/hide control anywhere.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/react';
import {
  ArrowRightOnRectangleIcon,
  Bars3Icon,
  Cog6ToothIcon,
  QuestionMarkCircleIcon,
  UserGroupIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { CheckIcon, ChevronDownIcon } from '@heroicons/react/20/solid';
import NotificationBell from './notifications/NotificationBell';
import { TRAINING_GUIDES_URL } from './home/helpLinks';
import SettingsModal from './SettingsModal';
import { useMyProfile } from '../hooks/useMyProfile';
import { useActingAsContext } from '../contexts/ActingAsContext';
import { useSidebar } from '../contexts/SidebarContext';
import { CHROME_CONTROL_CLASS } from './shared/chromeControl';
import { extractRoleNames, isMechanicalClientTenant, type RoleBearingUser } from '../utils/permissions';
import { TEXT } from './shared/Typography';

export interface TopBarUser {
  token?: string;
  fullName?: string;
  name?: string;
  displayName?: string;
  email?: string;
  accountName?: string;
  account?: string;
  company?: string;
  orgName?: string;
  organization?: string;
  /** The signed-in roles, for the Approval Desk landing choice in Settings. */
  roles?: string[];
}

interface TopBarProps {
  user: TopBarUser;
  onLogout: () => void;
  /** The logo goes Home. */
  onHome: () => void;
  /** Called before the bell navigates — App ends its landing hold here. */
  onNavigate?: () => void;
  /** The current screen's name, for screens with no heading of their own. */
  screenName?: string | null;
}

export function displayNameOf(user: TopBarUser | undefined): string {
  return user?.fullName || user?.name || user?.displayName || user?.email || 'User';
}

export function initialsOf(user: TopBarUser | undefined): string {
  const name = user?.name || user?.displayName || '';
  const email = user?.email || '';
  const base = name.trim() || email.split('@')[0] || 'U';
  const parts = base.trim().split(/\s+/).slice(0, 2);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[1][0]).toUpperCase();
}

/** External client accounts carry an organisation; staff do not. */
function accountOf(user: TopBarUser | undefined): string {
  return (
    user?.accountName || user?.account || user?.company || user?.orgName || user?.organization || ''
  );
}

const itemCls =
  'flex w-full items-center gap-3 px-4 py-3 text-left text-base text-gray-700 transition-colors duration-150 data-[focus]:bg-gray-50 sm:py-2.5';

function ProfileMenu({
  user,
  jobTitle,
  onLogout,
  onSettings,
}: {
  user: TopBarUser;
  jobTitle: string | null;
  onLogout: () => void;
  onSettings: () => void;
}) {
  const name = displayNameOf(user);
  const subtitle = jobTitle || user.email || '';
  const account = accountOf(user);

  return (
    <Menu as="div" className="relative">
      {/* Name and job title first, the avatar at the far right edge, with the
          open/closed caret as a small badge on the avatar rather than a
          separate arrow. */}
      <MenuButton
        data-account-menu
        aria-label={`Account menu for ${name}`}
        className={`group flex min-h-[44px] items-center gap-3 rounded-full py-1 pl-1 pr-1 sm:pl-4 ${CHROME_CONTROL_CLASS}`}
      >
        <span className="hidden min-w-0 max-w-[14rem] text-right sm:block">
          <span className="block truncate text-sm font-semibold leading-tight text-white">
            {name}
          </span>
          {subtitle && (
            <span className="block truncate text-xs leading-tight text-blue-200">{subtitle}</span>
          )}
        </span>
        <span className="relative flex-shrink-0">
          <span
            aria-hidden
            className="flex h-9 w-9 items-center justify-center rounded-full bg-white text-sm font-bold text-chrome-700 ring-1 ring-white ring-offset-2 ring-offset-chrome-700"
          >
            {initialsOf(user)}
          </span>
          <span
            aria-hidden
            className="absolute -bottom-1 -right-1 flex h-4 w-4 items-center justify-center rounded-full border border-white bg-chrome-700"
          >
            <ChevronDownIcon className="h-2.5 w-2.5 text-white transition-transform duration-150 group-data-[open]:rotate-180 motion-reduce:transition-none" />
          </span>
        </span>
      </MenuButton>

      <MenuItems
        anchor="bottom end"
        className="z-[120] w-72 max-w-[calc(100vw-1.5rem)] rounded-lg border border-gray-200 bg-white py-1 shadow-md focus:outline-none [--anchor-gap:6px]"
      >
        <div className="border-b border-gray-100 px-4 py-3">
          <p className={`truncate ${TEXT.emphasis}`}>{name}</p>
          {jobTitle && <p className={`truncate ${TEXT.bodyMuted}`}>{jobTitle}</p>}
          {user.email && <p className={`truncate ${TEXT.helper}`}>{user.email}</p>}
          {account && <p className={`truncate ${TEXT.caption}`}>{account}</p>}
        </div>
        <MenuItem>
          <button type="button" onClick={onSettings} className={itemCls}>
            <Cog6ToothIcon className="h-5 w-5 text-gray-400" aria-hidden />
            Settings
          </button>
        </MenuItem>
        <MenuItem>
          <button
            type="button"
            onClick={onLogout}
            className={`${itemCls} data-[focus]:text-red-700`}
          >
            <ArrowRightOnRectangleIcon className="h-5 w-5 text-gray-400" aria-hidden />
            Log out
          </button>
        </MenuItem>
      </MenuItems>
    </Menu>
  );
}

/**
 * "Acting as", shown only while the current page supports it (a page renders
 * <ActingAs/>, see contexts/ActingAsContext.tsx). Acting for somebody else is
 * consequential, so that state is amber and names the person, never subtle.
 */
function ActingAsMenu() {
  const { slot } = useActingAsContext();
  if (!slot) return null;
  const actingFor = slot.value
    ? slot.delegates.find(d => d.userId === slot.value)?.name
    : undefined;
  const people = [...slot.delegates].sort((a, b) =>
    (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' })
  );
  const options = [{ userId: '', name: slot.selfLabel }, ...people];

  const buttonCls = actingFor
    ? 'border border-amber-300 bg-amber-400/25 text-white hover:bg-amber-400/35 data-[open]:bg-amber-400/35 ' +
      'focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-chrome-700'
    : CHROME_CONTROL_CLASS;

  return (
    <Menu as="div" className="relative">
      <MenuButton
        aria-label={
          actingFor
            ? `Acting as ${actingFor}. Change who you are acting as`
            : 'Acting as yourself. Act on behalf of someone'
        }
        className={`group flex min-h-[44px] items-center gap-2 rounded-full py-1 pl-3 pr-2.5 transition-colors duration-150 ${buttonCls}`}
      >
        <UserGroupIcon
          className={`h-5 w-5 flex-shrink-0 ${actingFor ? 'text-amber-200' : ''}`}
          aria-hidden
        />
        <span className="hidden min-w-0 max-w-[12rem] text-left leading-tight md:block">
          <span
            className={`block text-2xs font-semibold uppercase tracking-wider ${actingFor ? 'text-amber-200' : 'text-blue-200'}`}
          >
            Acting as
          </span>
          <span className="block truncate text-sm font-semibold">
            {actingFor || slot.selfLabel}
          </span>
        </span>
        <ChevronDownIcon
          className="h-4 w-4 flex-shrink-0 opacity-80 transition-transform duration-150 group-data-[open]:rotate-180 motion-reduce:transition-none"
          aria-hidden
        />
      </MenuButton>
      <MenuItems
        anchor="bottom end"
        className="z-[120] w-72 max-w-[calc(100vw-1.5rem)] rounded-lg border border-gray-200 bg-white py-1 shadow-md focus:outline-none [--anchor-gap:6px]"
      >
        <p className={`border-b border-gray-100 px-4 py-2.5 ${TEXT.overline}`}>
          Act on behalf of
        </p>
        <div className="max-h-[60vh] overflow-y-auto py-1">
          {options.map(o => {
            const selected = o.userId === slot.value;
            return (
              <MenuItem key={o.userId || 'self'}>
                <button
                  type="button"
                  onClick={() => slot.onChange(o.userId)}
                  aria-current={selected ? 'true' : undefined}
                  className={`${itemCls} justify-between ${selected ? 'font-semibold text-gray-900' : ''}`}
                >
                  <span className="truncate">{o.name}</span>
                  {selected && (
                    <CheckIcon className="h-5 w-5 flex-shrink-0 text-blue-600" aria-hidden />
                  )}
                </button>
              </MenuItem>
            );
          })}
        </div>
      </MenuItems>
    </Menu>
  );
}

/** Tracks the xl breakpoint, where the sidebar is a pinned column rather than a drawer. */
function useIsDesktop(): boolean {
  const query = '(min-width: 1280px)';
  const [desktop, setDesktop] = useState(
    () => typeof window !== 'undefined' && !!window.matchMedia?.(query)?.matches
  );
  useEffect(() => {
    const mql = window.matchMedia?.(query);
    if (!mql?.addEventListener) return undefined;
    const on = (e: MediaQueryListEvent) => setDesktop(e.matches);
    mql.addEventListener('change', on);
    return () => mql.removeEventListener('change', on);
  }, []);
  return desktop;
}

export default function TopBar({ user, onLogout, onHome, onNavigate, screenName }: TopBarProps) {
  const { isSidebarOpen, setIsSidebarOpen, isSidebarCollapsed, setIsSidebarCollapsed } =
    useSidebar();
  const isDesktop = useIsDesktop();
  // One intent, two mechanisms: collapse the pinned column, or open the drawer.
  const menuShown = isDesktop ? !isSidebarCollapsed : isSidebarOpen;
  const toggleMenu = useCallback(() => {
    if (isDesktop) setIsSidebarCollapsed(!isSidebarCollapsed);
    else setIsSidebarOpen(!isSidebarOpen);
  }, [isDesktop, isSidebarCollapsed, isSidebarOpen, setIsSidebarCollapsed, setIsSidebarOpen]);

  // "[" toggles the menu, as in the editors people already use. Ignored while typing.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== '[' || e.metaKey || e.ctrlKey || e.altKey) return;
      const t = e.target as HTMLElement | null;
      const tag = t?.tagName?.toLowerCase();
      if (tag === 'input' || tag === 'textarea' || tag === 'select' || t?.isContentEditable) return;
      e.preventDefault();
      toggleMenu();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [toggleMenu]);
  const { profile } = useMyProfile(user.token);
  const { jobTitle } = profile;
  const [showSettings, setShowSettings] = useState(false);
  const barRef = useRef<HTMLElement>(null);
  // The guides are GRPS's internal SharePoint: an external client tenant could not open them.
  const showHelp = !isMechanicalClientTenant(extractRoleNames(user as RoleBearingUser));

  // The menu has unmounted by the time Settings closes, so hand focus back to
  // the account button explicitly rather than to the document body.
  const closeSettings = () => {
    setShowSettings(false);
    barRef.current?.querySelector<HTMLButtonElement>('[data-account-menu]')?.focus();
  };

  return (
    <>
      <header
        ref={barRef}
        className="relative z-30 flex-shrink-0 bg-chrome-700 pt-safe-mobile px-safe text-white"
      >
        {/* The sidebar's grid, continuous across the seam (index.css .chrome-grid). */}
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 opacity-[0.04] chrome-grid"
        />
        {/* A faint sheen from the top, and a fine highlight along the bottom
            edge that fades out at both ends. */}
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 bg-gradient-to-b from-white/[0.05] to-transparent"
        />
        <div
          aria-hidden
          className="pointer-events-none absolute inset-x-0 bottom-0 h-px bg-gradient-to-r from-white/0 via-white/30 to-white/0"
        />
        <div className="relative flex h-14 items-center gap-2 px-3 sm:px-4">
          <button
            type="button"
            onClick={toggleMenu}
            aria-label={menuShown ? 'Close menu' : 'Open menu'}
            title={`${menuShown ? 'Close' : 'Open'} menu ([)`}
            aria-expanded={menuShown}
            className={`inline-flex h-11 w-11 items-center justify-center rounded-full ${CHROME_CONTROL_CLASS}`}
          >
            {menuShown && !isDesktop ? (
              <XMarkIcon className="h-5 w-5" aria-hidden />
            ) : (
              <Bars3Icon className="h-5 w-5" aria-hidden />
            )}
          </button>

          {/* Shrinks before anything on the right does: the account menu
              (the only Log out) must never be pushed off a narrow phone. */}
          <button
            type="button"
            onClick={onHome}
            aria-label="GRAutomates home"
            className={`${screenName ? 'hidden sm:flex' : 'flex'} min-h-[44px] min-w-0 items-center gap-2.5 rounded-lg pr-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-chrome-700`}
          >
            <span className="truncate text-xl font-bold leading-tight tracking-tight">
              GR<span className="text-blue-200">Automates</span>
            </span>
          </button>
          {/* Below xl the sidebar is a closed drawer, so the bar names the
              screen. On a phone the name stands in for the wordmark. */}
          {screenName && (
            <h1 className="min-w-0 truncate text-base font-semibold text-white/90 sm:border-l sm:border-white/20 sm:pl-3 xl:hidden">
              {screenName}
            </h1>
          )}

          <div className="ml-auto flex flex-shrink-0 items-center gap-2 sm:gap-3">
            <ActingAsMenu />
            {/* Help: the GRAutomates training guides on SharePoint, in a new tab. */}
            {showHelp && (
              <a
                href={TRAINING_GUIDES_URL}
                target="_blank"
                rel="noopener noreferrer"
                title="Help — training guides"
                className={`inline-flex h-11 w-11 items-center justify-center rounded-full ${CHROME_CONTROL_CLASS}`}
              >
                <QuestionMarkCircleIcon className="h-6 w-6" aria-hidden />
                <span className="sr-only">Help: training guides (opens SharePoint in a new tab)</span>
              </a>
            )}
            <NotificationBell token={user.token} onNavigate={onNavigate} />
            <span aria-hidden className="hidden h-7 w-px bg-white/20 sm:block" />
            <ProfileMenu
              user={user}
              jobTitle={jobTitle}
              onLogout={onLogout}
              onSettings={() => setShowSettings(true)}
            />
          </div>
        </div>
      </header>

      {/* Portalled to the end of <body>: inside the header's z-30 stacking
          context, or anywhere before the sidebar in the DOM, the sidebar
          (z-50) would paint over the modal. */}
      {createPortal(
        showSettings ? (
          <SettingsModal
            isOpen
            onClose={closeSettings}
            token={user.token}
            roles={extractRoleNames(user as RoleBearingUser)}
          />
        ) : null,
        document.body
      )}
    </>
  );
}
